import { ViewMode } from './types'

// Handle date select
export function handleDateSelect(
  day: number,
  currentYear: number,
  currentMonth: number,
  includeTime: boolean,
  selectedTime: { hours: number; minutes: number },
  setSelectedDate: React.Dispatch<React.SetStateAction<Date | null>>,
  onChange: (value: string) => void,
  onClose: () => void,
  event?: React.MouseEvent
) {
  // Prevent event propagation to avoid any parent handlers
  if (event) {
    event.stopPropagation()
    event.preventDefault()
  }
  
  const date = new Date(currentYear, currentMonth, day)
  if (includeTime) {
    // Always use selectedTime state (which is initialized with current time if no value)
    date.setHours(selectedTime.hours, selectedTime.minutes)
  }
  setSelectedDate(date)
  
  // For fields with time, don't call onChange or close - wait for Confirm
  // For date-only fields, call onChange and close immediately
  if (!includeTime) {
    const formatted = `${String(day).padStart(2, '0')}/${String(currentMonth + 1).padStart(2, '0')}/${currentYear}`
    onChange(formatted)
    onClose()
  }
}

// Handle month select
export function handleMonthSelect(
  month: number,
  monthYearOnly: boolean,
  selectedYear: number | null,
  currentYear: number,
  setSelectedMonth: React.Dispatch<React.SetStateAction<number | null>>,
  setCurrentMonth: React.Dispatch<React.SetStateAction<number>>,
  setSelectedYear: React.Dispatch<React.SetStateAction<number | null>>,
  setViewMode: React.Dispatch<React.SetStateAction<ViewMode>>,
  onChange: (value: string) => void,
  onClose: () => void
) {
  setSelectedMonth(month)
  setCurrentMonth(month)
  if (monthYearOnly) {
    // If only month/year selection, use currentYear if year not explicitly selected
    const yearToUse = selectedYear !== null ? selectedYear : currentYear
    setSelectedYear(yearToUse)
    // Save immediately with selected month and year
    const formatted = `${String(month + 1).padStart(2, '0')}/${yearToUse}`
    onChange(formatted)
    onClose()
  } else {
    // Switch to day view
    setViewMode('day')
  }
}

// Handle year select
export function handleYearSelect(
  year: number,
  monthYearOnly: boolean,
  selectedMonth: number | null,
  setSelectedYear: React.Dispatch<React.SetStateAction<number | null>>,
  setCurrentYear: React.Dispatch<React.SetStateAction<number>>,
  setViewMode: React.Dispatch<React.SetStateAction<ViewMode>>,
  onChange: (value: string) => void,
  onClose: () => void
) {
  setSelectedYear(year)
  setCurrentYear(year)
  if (monthYearOnly) {
    // If only month/year selection, we need month to be selected first
    if (selectedMonth !== null) {
      const formatted = `${String(selectedMonth + 1).padStart(2, '0')}/${year}`
      onChange(formatted)
      onClose()
    } else {
      // Switch to month view to select month
      setViewMode('month')
    }
  } else {
    // Switch to month view
    setViewMode('month')
  }
}

// Handle present
export function handlePresent(
  onChange: (value: string) => void,
  onClose: () => void
) {
  onChange('Present')
  onClose()
}

// Handle time change
export function handleTimeChange(
  hours: number,
  minutes: number,
  selectedDate: Date | null,
  setSelectedTime: React.Dispatch<React.SetStateAction<{ hours: number; minutes: number }>>,
  setSelectedDate: React.Dispatch<React.SetStateAction<Date | null>>,
  setCurrentMonth: React.Dispatch<React.SetStateAction<number>>,
  setCurrentYear: React.Dispatch<React.SetStateAction<number>>
) {
  setSelectedTime({ hours, minutes })
  if (selectedDate) {
    const date = new Date(selectedDate)
    date.setHours(hours, minutes)
    setSelectedDate(date)
  } else {
    // If no date selected yet, use current date with selected time
    const now = new Date()
    const date = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes)
    setSelectedDate(date)
    setCurrentMonth(now.getMonth())
    setCurrentYear(now.getFullYear())
  }
  // Don't call onChange when time changes - only update internal state
  // onChange will be called only on Confirm
}

// Handle confirm
export function handleConfirm(
  selectedDate: Date | null,
  selectedTime: { hours: number; minutes: number },
  includeTime: boolean,
  onChange: (value: string) => void,
  onConfirm?: (value: string) => void,
  onClose: () => void
) {
  if (selectedDate) {
    if (includeTime) {
      const formatted = `${String(selectedDate.getDate()).padStart(2, '0')}/${String(selectedDate.getMonth() + 1).padStart(2, '0')}/${selectedDate.getFullYear()}, ${String(selectedTime.hours).padStart(2, '0')}:${String(selectedTime.minutes).padStart(2, '0')}`
      // Call onConfirm if provided, otherwise call onChange
      if (onConfirm) {
        onConfirm(formatted)
      } else {
        onChange(formatted)
      }
    } else {
      const formatted = `${String(selectedDate.getDate()).padStart(2, '0')}/${String(selectedDate.getMonth() + 1).padStart(2, '0')}/${selectedDate.getFullYear()}`
      onChange(formatted)
    }
  }
  onClose()
}

// Handle prev month
export function handlePrevMonth(
  currentMonth: number,
  currentYear: number,
  setCurrentMonth: React.Dispatch<React.SetStateAction<number>>,
  setCurrentYear: React.Dispatch<React.SetStateAction<number>>
) {
  if (currentMonth === 0) {
    setCurrentMonth(11)
    setCurrentYear(currentYear - 1)
  } else {
    setCurrentMonth(currentMonth - 1)
  }
}

// Handle next month
export function handleNextMonth(
  currentMonth: number,
  currentYear: number,
  setCurrentMonth: React.Dispatch<React.SetStateAction<number>>,
  setCurrentYear: React.Dispatch<React.SetStateAction<number>>
) {
  if (currentMonth === 11) {
    setCurrentMonth(0)
    setCurrentYear(currentYear + 1)
  } else {
    setCurrentMonth(currentMonth + 1)
  }
}

// Handle prev year
export function handlePrevYear(
  currentYear: number,
  setCurrentYear: React.Dispatch<React.SetStateAction<number>>
) {
  setCurrentYear(currentYear - 1)
}

// Handle next year
export function handleNextYear(
  currentYear: number,
  setCurrentYear: React.Dispatch<React.SetStateAction<number>>
) {
  setCurrentYear(currentYear + 1)
}

// Handle prev decade
export function handlePrevDecade(
  currentYear: number,
  setCurrentYear: React.Dispatch<React.SetStateAction<number>>
) {
  setCurrentYear(currentYear - 10)
}

// Handle next decade
export function handleNextDecade(
  currentYear: number,
  setCurrentYear: React.Dispatch<React.SetStateAction<number>>
) {
  setCurrentYear(currentYear + 10)
}

// Handle today
export function handleToday(
  includeTime: boolean,
  setCurrentMonth: React.Dispatch<React.SetStateAction<number>>,
  setCurrentYear: React.Dispatch<React.SetStateAction<number>>,
  setSelectedTime: React.Dispatch<React.SetStateAction<{ hours: number; minutes: number }>>,
  setSelectedDate: React.Dispatch<React.SetStateAction<Date | null>>,
  handleDateSelect: (day: number, event?: React.MouseEvent) => void
) {
  const today = new Date()
  setCurrentMonth(today.getMonth())
  setCurrentYear(today.getFullYear())
  if (includeTime) {
    const currentTime = { hours: today.getHours(), minutes: today.getMinutes() }
    setSelectedTime(currentTime)
    // For time fields, set the date but don't call onChange - user needs to click Confirm
    const date = new Date(today.getFullYear(), today.getMonth(), today.getDate(), currentTime.hours, currentTime.minutes)
    setSelectedDate(date)
    // Don't close - let user adjust or click Confirm
  } else {
    // For date-only fields, use the existing handleDateSelect which closes immediately
    handleDateSelect(today.getDate())
  }
}
